import {type AppliedTheme, THEME_AUTO, THEME_DARK, THEME_LIGHT, THEMES, type Theme} from "../redux/themeSlice";

const KEY_STORAGE_THEME = "reva_theme";

/**
 * Read the theme persisted in local storage. Only concrete themes are ever
 * stored; anything else (missing or invalid) falls back to "auto".
 */
export const getStoredTheme = (): Theme => {
    const storedTheme = localStorage.getItem(KEY_STORAGE_THEME);
    if (storedTheme === THEME_LIGHT || storedTheme === THEME_DARK) {
        return storedTheme;
    }
    return THEME_AUTO;
};

/**
 * Persist the chosen theme. "auto" is not stored as a value but removes any
 * existing preference, so the OS preference is followed again.
 */
export const setStoredTheme = (theme: Theme): void => {
    if (theme === THEME_AUTO) {
        // Auto - remove preference
        localStorage.removeItem(KEY_STORAGE_THEME);
    } else {
        localStorage.setItem(KEY_STORAGE_THEME, theme);
    }
};

/** Resolve the OS-preferred theme via the `prefers-color-scheme` media query. */
const getPreferredTheme = (): typeof THEME_LIGHT | typeof THEME_DARK => {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? THEME_DARK : THEME_LIGHT;
};

/**
 * Resolve a (possibly "auto") theme to the concrete PrimeReact theme name to apply.
 */
export const getThemeToApply = (theme: Theme): AppliedTheme => {
    const effectiveTheme = theme === THEME_AUTO ? getPreferredTheme() : theme;
    return THEMES[effectiveTheme];
};
